import type { PayloadInterface } from '~/types/AppData/AppDataInterface';
import { Logger } from '~/utilities/logger';
import type { AnalyticsType } from '~/types/AnalyticsType';

/**
 * The `Analytics` class is responsible for managing and formatting analytics-related data
 * and events for transmission. It utilizes a constructor to initialize the payload
 * containing property settings, experiments, and viewer details. The class provides
 * functionality to construct structured event data through the `getEvent` method.
 * Events include information such as property, experiments, language, page type, platform,
 * and user-related cookies. These prepared events are intended for logging or external
 * analytics services like `dataLayer`.
 */
export class Analytics {
  private payload: PayloadInterface;

  constructor(payload: PayloadInterface) {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    this.payload = payload;
  }

  public getEvent(event: AnalyticsType['event']) {
    let eventCategory = '';

    // TODO remove test-new-homepage after testing
    if (
      this.payload.canonicalURL === 'https://www.si.com/' ||
      this.payload.canonicalURL === 'https://www.si.com/test-new-homepage-v2'
    ) {
      eventCategory = 'Home Page';
    }

    return {
      GA_event_category: eventCategory,
      event: 'GTM event To GA',

      // Don't include properties with undefined values
      ...Object.fromEntries(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-unnecessary-condition
        Object.entries(event).filter(([_, value]) => value !== undefined)
      ),
    };
  }
}

/**
 * This function, `sendAnalyticsEvent`, is responsible for creating an instance
 * of the `Analytics` class using the provided `payload` and constructing an analytics
 * event based on the provided `event` data.
 *
 * It logs the event details using the `Logger` utility and outputs the structured
 * event data to the console. The function is designed for managing analytics events
 * efficiently and can integrate with external analytics platforms, such as `dataLayer`,
 * if needed (commented-out example present in the code).
 *
 * @param event - An object of type `AnalyticsType["event"]` that contains event-specific information.
 * @param payload - The `PayloadInterface` object containing property-specific settings,
 * experiments, and viewer-related details.
 */
export function sendAnalyticsEvent(
  event: AnalyticsType['event'],
  payload: PayloadInterface
) {
  // Push to GA
  const AnalyticsInstance = new Analytics(payload);
  window.dataLayer.push(AnalyticsInstance.getEvent(event));

  // Add info event to logger
  const Log = new Logger();
  Log.info({
    message: 'event',
    event,
    full: AnalyticsInstance.getEvent(event),
  });
}
